/**
 * Theme Error Handler
 * Categorizes and handles theme loading errors
 */

/**
 * Theme error types
 */
export const ThemeErrorType = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  DOMAIN_NOT_FOUND: 'DOMAIN_NOT_FOUND',
  INVALID_RESPONSE: 'INVALID_RESPONSE',
  TIMEOUT: 'TIMEOUT',
  UNAUTHORIZED: 'UNAUTHORIZED',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN'
};

/**
 * Categorize an error
 *
 * @param {Error} error - The error to categorize
 * @returns {string} The error type
 */
export function categorizeError(error) {
  // Network errors (no response)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return ThemeErrorType.TIMEOUT;
    }
    return ThemeErrorType.NETWORK_ERROR;
  }

  // HTTP status errors
  const status = error.response.status;

  if (status === 401 || status === 403) {
    return ThemeErrorType.UNAUTHORIZED;
  }

  if (status === 404) {
    return ThemeErrorType.DOMAIN_NOT_FOUND;
  }

  if (status >= 500) {
    return ThemeErrorType.SERVER_ERROR;
  }

  if (status >= 400) {
    return ThemeErrorType.INVALID_RESPONSE;
  }

  return ThemeErrorType.UNKNOWN;
}

/**
 * Get user-friendly error message
 *
 * @param {Error} error - The error object
 * @param {string} errorType - The categorized error type
 * @returns {string} User-friendly message
 */
export function getErrorMessage(error, errorType = null) {
  const type = errorType || categorizeError(error);

  const messages = {
    [ThemeErrorType.NETWORK_ERROR]: 'Unable to connect to the server. Please check your internet connection.',
    [ThemeErrorType.DOMAIN_NOT_FOUND]: 'Organization branding not found. Using default theme.',
    [ThemeErrorType.INVALID_RESPONSE]: 'Invalid branding data received. Using default theme.',
    [ThemeErrorType.TIMEOUT]: 'Request timed out. Please try again.',
    [ThemeErrorType.UNAUTHORIZED]: 'Authentication required to load organization branding.',
    [ThemeErrorType.SERVER_ERROR]: 'Server error occurred. Using cached or default theme.',
    [ThemeErrorType.UNKNOWN]: 'An unexpected error occurred while loading theme.'
  };

  return messages[type] || messages[ThemeErrorType.UNKNOWN];
}

/**
 * Determine recovery strategy for error
 *
 * @param {string} errorType - The error type
 * @returns {Object} Recovery strategy
 */
export function getRecoveryStrategy(errorType) {
  const strategies = {
    [ThemeErrorType.NETWORK_ERROR]: {
      retry: true,
      maxRetries: 3,
      useCache: true,
      useFallback: true
    },
    [ThemeErrorType.DOMAIN_NOT_FOUND]: {
      retry: false,
      useCache: false,
      useFallback: true
    },
    [ThemeErrorType.INVALID_RESPONSE]: {
      retry: false,
      useCache: true,
      useFallback: true
    },
    [ThemeErrorType.TIMEOUT]: {
      retry: true,
      maxRetries: 2,
      useCache: true,
      useFallback: true
    },
    [ThemeErrorType.UNAUTHORIZED]: {
      retry: false,
      useCache: true,
      useFallback: true
    },
    [ThemeErrorType.SERVER_ERROR]: {
      retry: true,
      maxRetries: 2,
      useCache: true,
      useFallback: true
    },
    [ThemeErrorType.UNKNOWN]: {
      retry: false,
      useCache: true,
      useFallback: true
    }
  };

  return strategies[errorType] || strategies[ThemeErrorType.UNKNOWN];
}

/**
 * Log error for debugging
 *
 * @param {string} context - The error context
 * @param {Error} error - The error object
 * @param {Object} metadata - Additional metadata
 */
export function logError(context, error, metadata = {}) {
  const errorType = categorizeError(error);
  const message = getErrorMessage(error, errorType);

  const errorData = {
    context,
    type: errorType,
    message,
    originalError: error.message,
    timestamp: new Date().toISOString(),
    ...metadata
  };

  // Log to console (in production, this could send to error tracking service)
  if (process.env.NODE_ENV === 'development') {
    console.error('[Theme Error]', errorData);
  } else {
    console.warn('[Theme Error]', errorData.type, errorData.message);
  }

  return errorData;
}

/**
 * Theme Error Handler class
 */
class ThemeErrorHandler {
  constructor() {
    this.errorLog = [];
    this.maxLogSize = 50;
  }

  /**
   * Handle a theme error
   *
   * @param {Error} error - The error to handle
   * @param {Object} options - Handler options
   * @returns {Object} Error handling result
   */
  handleError(error, options = {}) {
    const {
      context = 'theme-loading',
      domain = null,
      retry = false
    } = options;

    const errorType = categorizeError(error);
    const message = getErrorMessage(error, errorType);
    const strategy = getRecoveryStrategy(errorType);

    // Log error
    const errorData = logError(context, error, { domain });

    // Add to error log
    this.errorLog.unshift(errorData);
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog.pop();
    }

    return {
      type: errorType,
      message,
      strategy,
      shouldRetry: strategy.retry && !retry, // Don't retry if already retried
      useCache: strategy.useCache,
      useFallback: strategy.useFallback
    };
  }

  /**
   * Get error log
   *
   * @param {number} limit - Maximum number of errors to return
   * @returns {Array} Error log entries
   */
  getErrorLog(limit = 10) {
    return this.errorLog.slice(0, limit);
  }

  /**
   * Clear error log
   */
  clearErrorLog() {
    this.errorLog = [];
  }

  /**
   * Get error statistics
   *
   * @returns {Object} Error statistics
   */
  getErrorStats() {
    const stats = {
      total: this.errorLog.length,
      byType: {}
    };

    this.errorLog.forEach(error => {
      const type = error.type;
      stats.byType[type] = (stats.byType[type] || 0) + 1;
    });

    return stats;
  }
}

// Export singleton instance
export default new ThemeErrorHandler();

// Export class for custom instances
export { ThemeErrorHandler };
